import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Divider,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { addDoc, collection, getFirestore } from 'firebase/firestore';
// import { db } from '@utilities/firebase';
import { generateUniqueCode } from '@utilities/common';

interface RoomDetail {
  name: string;
  description: string;
  pricePerNight: number;
  maxGuests: number;
  totalRooms: number;
  amenities: string[];
  facilities: string[];
  imageUrl: string;
}

interface Guest {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
  address: string;
}

interface BookingDrawerProps {
  open: boolean;
  onClose: (isSuccess: boolean) => void;
  selectedRooms: { categoryId: string; quantity: number }[];
  searchParams: {
    checkIn: Date | null;
    checkOut: Date | null;
    guests: number;
    isSearched: boolean;
  };
  roomDetails: RoomDetail[];
  selectedRestaurant: any;
}

const _steps = ['Guest Information', 'Payment Method', 'Confirmation'];

const BookingDrawer: React.FC<BookingDrawerProps> = ({
  open,
  onClose,
  selectedRooms,
  searchParams,
  roomDetails,
  selectedRestaurant,
}) => {
  const [_activeStep, setActiveStep] = useState(0);
  const [guestInfo, setGuestInfo] = useState<Guest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
    address: '',
  });
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'upi'>('cash');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const calculateTotalAmount = () => {
    if (!searchParams.checkIn || !searchParams.checkOut) return 0;

    const checkIn = dayjs(searchParams.checkIn);
    const checkOut = dayjs(searchParams.checkOut);
    const nights = checkOut.diff(checkIn, 'day') + 1;

    return selectedRooms.reduce((total, room) => {
      const roomDetail = roomDetails.find((rd: RoomDetail) => rd.name === room.categoryId);
      return total + (roomDetail?.pricePerNight || 0) * room.quantity * nights;
    }, 0);
  };

  const getBookingSummary = () => {
    if (!searchParams.checkIn || !searchParams.checkOut) return null;

    const checkIn = dayjs(searchParams.checkIn);
    const checkOut = dayjs(searchParams.checkOut);
    const nights = checkOut.diff(checkIn, 'day') + 1;

    return {
      nights,
      totalRooms: selectedRooms.reduce((total, room) => total + room.quantity, 0),
      totalAmount: calculateTotalAmount(),
      checkInTime: checkIn.format('hh:mm A'),
      checkOutTime: checkOut.format('hh:mm A'),
      checkInDate: checkIn.format('MMM DD, YYYY'),
      checkOutDate: checkOut.format('MMM DD, YYYY'),
    };
  };

  const _handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const _handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (!searchParams.checkIn || !searchParams.checkOut || !selectedRestaurant?.docId) return;

    try {
      setIsSubmitting(true);
      const checkIn = dayjs(searchParams.checkIn);
      const checkOut = dayjs(searchParams.checkOut);
      // Create the booking data
      const bookingData = {
        restaurantId: selectedRestaurant.docId,
        guestInfo: {
          firstName: guestInfo?.firstName || '',
          lastName: guestInfo?.lastName || '',
          email: guestInfo?.email || '',
          phone: guestInfo?.phone || '8787878787',
          specialRequests: guestInfo?.specialRequests || '',
          address: guestInfo?.address || '',
        },
        rooms: selectedRooms.map(room => {
          const roomDetail = roomDetails.find(rd => rd.name === room.categoryId);
          return {
            name: roomDetail?.name || room.categoryId,
            quantity: room.quantity,
            pricePerNight: roomDetail?.pricePerNight || 0,
            maxGuests: roomDetail?.maxGuests || 2,
            amenities: roomDetail?.amenities || [],
            facilities: roomDetail?.facilities || [],
          };
        }),
        checkIn: searchParams?.checkIn,
        checkOut: searchParams?.checkOut,
        totalGuests: searchParams?.guests,
        totalAmount: calculateTotalAmount(),
        nights: checkOut?.diff(checkIn, 'day') + 1,
        paymentMethod,
        status: 'pending' as const,
        createdAt: new Date(),
        updatedAt: new Date(),
        uoid: generateUniqueCode(6),
      };

      // Add to Firestore
      const db = getFirestore();
      const _docRef = await addDoc(collection(db, 'room-bookings'), bookingData);

      // Reset form and close drawer
      onClose(true);
      setActiveStep(0);
      setGuestInfo({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        specialRequests: '',
        address: '',
      });
      setPaymentMethod('cash');
    } catch (error) {
      console.error('Error creating booking:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  const _renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={guestInfo.firstName}
                onChange={(e) =>
                  setGuestInfo({ ...guestInfo, firstName: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={guestInfo.lastName}
                onChange={(e) =>
                  setGuestInfo({ ...guestInfo, lastName: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={guestInfo.email}
                onChange={(e) =>
                  setGuestInfo({ ...guestInfo, email: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={guestInfo.phone}
                onChange={(e) =>
                  setGuestInfo({ ...guestInfo, phone: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={guestInfo.address}
                onChange={(e) =>
                  setGuestInfo({ ...guestInfo, address: e.target.value })
                }
                required
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <FormControl fullWidth required>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'card' | 'upi')}
              label="Payment Method"
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
            </Select>
          </FormControl>
        );
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Booking Summary
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Stay Details
              </Typography>
              {getBookingSummary() && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Check-in</Typography>
                    <Typography variant="body2">
                      {getBookingSummary()?.checkInDate} {getBookingSummary()?.checkInTime}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Check-out</Typography>
                    <Typography variant="body2">
                      {getBookingSummary()?.checkOutDate} {getBookingSummary()?.checkOutTime}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Nights</Typography>
                    <Typography variant="body2">{getBookingSummary()?.nights} nights</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Selected Rooms</Typography>
                    <Typography variant="body2">{getBookingSummary()?.totalRooms}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="subtitle1">Total Amount</Typography>
                    <Typography variant="subtitle1" color="primary">
                      ₹{getBookingSummary()?.totalAmount.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Guest Information
            </Typography>
            <Typography>
              {guestInfo.firstName} {guestInfo.lastName}
            </Typography>
            <Typography>{guestInfo.email}</Typography>
            <Typography>{guestInfo.phone}</Typography>
            {guestInfo.specialRequests && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Special Requests: {guestInfo.specialRequests}
              </Typography>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const _isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return guestInfo.firstName && guestInfo.lastName && guestInfo.email && guestInfo.phone && guestInfo.address;
      case 1:
        return true;
      default:
        return true;
    }
  };

  const isFormValid = () => {
    return guestInfo.firstName && 
           guestInfo.lastName && 
           guestInfo.email && 
           guestInfo.phone && 
           guestInfo.address;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => onClose(false)}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 400 } }
      }}
    >
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          Complete Booking
        </Typography>

        {/* Guest Information Form */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                fullWidth
                value={guestInfo.firstName}
                onChange={(e) => setGuestInfo({ ...guestInfo, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                fullWidth
                value={guestInfo.lastName}
                onChange={(e) => setGuestInfo({ ...guestInfo, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                fullWidth
                type="email"
                value={guestInfo.email}
                onChange={(e) => setGuestInfo({ ...guestInfo, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Phone"
                fullWidth
                value={guestInfo.phone}
                onChange={(e) => setGuestInfo({ ...guestInfo, phone: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                multiline
                rows={3}
                value={guestInfo.address}
                onChange={(e) => setGuestInfo({ ...guestInfo, address: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value as 'cash' | 'card' | 'upi')}
                  label="Payment Method"
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Booking Summary */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" gutterBottom>
            Booking Summary
          </Typography>
          {getBookingSummary() && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Check-in</Typography>
                <Typography variant="body2">
                  {getBookingSummary()?.checkInDate} {getBookingSummary()?.checkInTime}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Check-out</Typography>
                <Typography variant="body2">
                  {getBookingSummary()?.checkOutDate} {getBookingSummary()?.checkOutTime}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Nights</Typography>
                <Typography variant="body2">{getBookingSummary()?.nights} nights</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Selected Rooms</Typography>
                <Typography variant="body2">{getBookingSummary()?.totalRooms}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="subtitle1">Total Amount</Typography>
                <Typography variant="subtitle1" color="primary">
                  ₹{getBookingSummary()?.totalAmount.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => onClose(false)}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth
            disabled={!isFormValid() || isSubmitting}
          >
            {isSubmitting ? 'Creating Booking...' : 'Confirm Booking'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

const mapStateToProps = (state: any) => {
  return {
    roomDetails: state.common?.selectedRestaurant?.roomDetails || [],
    selectedRestaurant: state.common?.selectedRestaurant
  };
};

export default connect(mapStateToProps)(BookingDrawer);